import React, { useMemo, useState } from "react";
import { isNum } from "./paeiPrimitives";

// CAPI analysis — who holds Authority, Power and Influence on this process.
//
// ONE RULE, and the whole tab is built around it: nothing here is derived. The
// holders, the coalition and the findings are decisions the API already made,
// using `standing` and `process_needs` — neither of which can be recovered from
// the capacity column. Sorting that column and taking the top is exactly how the
// Rector came to be shown holding all three when the API says Power is the
// Principal's: the Rector scores 90, but he is never Responsible here, so his
// standing is 0% and there is no cooperation of his to withhold.
//
// So: read `capi.holders`, render `capi.coalition.note` verbatim, and never
// infer either from `capi.jobs[]`. The jobs table exists to show the evidence
// behind the decision, not to make it.

const ENERGIES = ["authority", "power", "influence"];

const clamp100 = (v) => (isNum(v) ? Math.max(0, Math.min(100, Number(v))) : null);
const pct = (v) => (isNum(v) ? `${Math.round(Number(v))}%` : "—");
const num = (v) => (isNum(v) ? String(Math.round(Number(v))) : "—");

// `capi.jobs[]` carries capacity and standing per energy; the two plausible
// encodings are read the same way so a row renders either way.
function cellFor(row, energy) {
  const direct = row?.[energy];
  if (direct && typeof direct === "object") {
    return { capacity: clamp100(direct.capacity), standing: clamp100(direct.standing) };
  }
  return {
    capacity: clamp100(row?.capacity?.[energy] ?? (isNum(direct) ? direct : null)),
    standing: clamp100(row?.standing?.[energy]),
  };
}

/* ------------------------------- holder card ----------------------------- */

function HolderCard({ holder }) {
  const energy = holder?.energy;
  const held = holder?.job_id != null || !!holder?.job_name;

  return (
    <div className={`pa-capi-card dim-${energy}`}>
      <div className="pa-capi-card-label">Holds {holder?.label || energy}</div>

      {held ? (
        <>
          <div className="pa-capi-holder-name">{holder.job_name}</div>
          <div className="pa-capi-figures">
            <span className="pa-capi-score">{num(holder.capacity)}</span>
            <span className="pa-capi-standing">
              <b>{pct(holder.standing)}</b> standing
            </span>
          </div>
          {/* Capacity is what the job brings; standing is how much of that
              position this process actually gives them. The gap between the two
              is usually the finding, so they are never shown as one number. */}
          <div className="pa-capi-needs">
            this process needs {num(holder.process_needs)}
          </div>
        </>
      ) : (
        <div className="pa-capi-none">No job clears the bar for this energy here.</div>
      )}

      {holder?.means && <p className="pa-capi-blurb">{holder.means}</p>}
      {holder?.reason && <p className="pa-capi-reason">{holder.reason}</p>}

      {held && (holder.runners_up || []).length > 0 && (
        <div className="pa-capi-runners">
          <div className="pa-capi-runners-label">Next in line</div>
          <ul>
            {holder.runners_up.slice(0, 3).map((r, i) => (
              <li key={r.job_id ?? `${r.job_name}-${i}`}>
                <span className="pa-capi-runner-name" title={r.job_name}>{r.job_name}</span>
                <span className="pa-capi-runner-figures">
                  {num(r.capacity)}
                  <em>{pct(r.standing)}</em>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- tab ---------------------------------- */

function PaeiCapiTab({ result }) {
  const capi = result?.capi || null;
  const [query, setQuery] = useState("");

  const holders = capi?.holders || null;
  const coalition = capi?.coalition || null;
  const findings = Array.isArray(capi?.findings) ? capi.findings : [];
  const profile = capi?.process_profile || null;
  const coverage = capi?.coverage || null;
  const jobs = useMemo(() => (Array.isArray(capi?.jobs) ? capi.jobs : []), [capi]);

  // Which job the API named for each energy — used only to badge the table.
  const holderIds = useMemo(() => {
    const out = {};
    ENERGIES.forEach((e) => {
      const h = holders?.[e];
      if (!h) return;
      out[e] = { job_id: h.job_id ?? null, job_name: h.job_name || null };
    });
    return out;
  }, [holders]);

  const isHolder = (row, energy) => {
    const h = holderIds[energy];
    if (!h) return false;
    if (h.job_id != null && row?.job_id != null) return String(h.job_id) === String(row.job_id);
    return !!h.job_name && h.job_name === (row?.job_name || row?.name);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((j) => String(j.job_name || j.name || "").toLowerCase().includes(q));
  }, [jobs, query]);

  if (!capi) {
    return (
      <div className="pa-panel">
        <div className="pa-empty">
          This run returned no <code>capi</code> block. Authority, power and influence are decided by
          the analysis service, not by this page, so there is nothing to show until the run carries
          them.
        </div>
      </div>
    );
  }

  return (
    <div className="pa-panel">
      {/* ------------------------- process profile ------------------------- */}
      {profile && (
        <section className="pa-card pa-capi-profile">
          <div className="pa-capi-profile-head">
            <h3 className="pa-card-title">What this process demands</h3>
            {profile.dominant && (
              <span className={`pa-tag holder dim-${profile.dominant}`}>
                {profile.dominant_label || profile.dominant}-led
              </span>
            )}
          </div>
          <p className="pa-card-hint">
            The reference point for every number below: a capacity of 100 means nothing until you
            know what the work asks for.
          </p>

          <div className="pa-capi-needs-bars">
            {ENERGIES.map((e) => {
              const v = clamp100(profile.need?.[e]);
              return (
                <div className="pa-capi-need-row" key={e}>
                  <span className={`pa-capi-need-label dim-${e}`}>
                    {holders?.[e]?.label || e}
                  </span>
                  <span className="pa-capi-track">
                    <i className={`cap dim-${e}`} style={{ width: `${v ?? 0}%` }} />
                  </span>
                  <span className="pa-capi-val">{num(v)}</span>
                </div>
              );
            })}
          </div>

          {(profile.reasons || []).length > 0 && (
            <details className="pa-reasons">
              <summary>why this profile</summary>
              <ul>
                {profile.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {/* ---------------------------- holders ------------------------------ */}
      {holders && (
        <div className="pa-capi-grid">
          {ENERGIES.map((e) => (
            <HolderCard key={e} holder={holders[e] ? { energy: e, ...holders[e] } : { energy: e }} />
          ))}
        </div>
      )}

      {/* --------------------------- coalition ----------------------------- */}
      {coalition && (
        <div className={`pa-statusbar ${coalition.size === 1 ? "tone-ok" : "tone-warn"}`}>
          <span className="pa-statusbar-tag">
            {coalition.size === 1 ? "one holder" : `coalition of ${coalition.size}`}
          </span>
          <div className="pa-statusbar-body">
            {/* Verbatim: whether a change can be made alone is the API's call,
                not a shape this page infers from who tops which column. */}
            <p className="pa-verbatim">{coalition.note}</p>

            {(coalition.members || []).length > 0 && (
              <div className="pa-capi-members">
                {coalition.members.map((m, i) => (
                  <span className="pa-capi-member" key={m.job_id ?? `${m.job_name}-${i}`}>
                    <b>{m.job_name}</b>
                    {(m.brings || []).map((b) => (
                      <em key={b} className={`dim-${String(b).toLowerCase()}`}>{b}</em>
                    ))}
                  </span>
                ))}
              </div>
            )}

            {(coalition.uncovered || []).length > 0 && (
              <p>
                <b>{coalition.uncovered.join(", ")}</b>{" "}
                {coalition.uncovered.length === 1 ? "is" : "are"} supplied by nobody on this process
                — no combination of these jobs covers it.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ---------------------------- findings ----------------------------- */}
      {findings.length > 0 && (
        <section className="pa-card">
          <h3 className="pa-card-title">What to do about it</h3>
          <div className="pa-capi-findings">
            {findings.map((f, i) => (
              <div className={`pa-capi-finding ${f.kind}`} key={`${f.kind}-${f.job_name}-${i}`}>
                <div className="pa-capi-finding-head">
                  <span className="pa-tag">{f.kind === "overstretched" ? "overstretched" : "untapped"}</span>
                  <b>{f.job_name}</b>
                  <span className="pa-muted">
                    {f.label} · capacity {num(f.capacity)} · standing {pct(f.standing)} · needs{" "}
                    {num(f.process_needs)}
                  </span>
                </div>
                <p className="pa-verbatim">{f.note}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------------------- coverage ----------------------------- */}
      {coverage && (
        <div className="pa-capi-coverage">
          <p>
            <b>
              {coverage.jobs_with_capi} of {coverage.jobs_on_process}
            </b>{" "}
            jobs on this process have a CAPI profile.
          </p>
          {/* Verbatim, and never relabelled: CMS answers 404 both for "no record"
              and for "not visible to your role", so the two are indistinguishable
              from here. This note is worded for exactly that. */}
          {coverage.note && <p className="pa-verbatim">{coverage.note}</p>}
          {(coverage.missing || []).length > 0 && (
            <div className="pa-missing-list">
              {coverage.missing.map((m, i) => (
                <span className="pa-missing-chip" key={m.job_id ?? `${m.name}-${i}`}>
                  {m.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------------------- jobs table --------------------------- */}
      {jobs.length > 0 && (
        <>
          <div className="pa-toolbar">
            <input
              type="search"
              className="pa-search"
              placeholder="Filter by job name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Filter jobs by name"
            />
            <div className="pa-capi-legend">
              <span><i className="cap" /> capacity — what the job brings</span>
              <span><i className="stand" /> standing — how much of it this process gives them</span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="pa-empty">No jobs match.</div>
          ) : (
            <div className="pa-tbl-scroll">
              <table className="pa-tbl pa-capi-tbl">
                <thead>
                  <tr>
                    <th>Job</th>
                    {ENERGIES.map((e) => (
                      <th key={e}>{holders?.[e]?.label || e}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.job_id ?? `${row.job_name}-${i}`}>
                      <td>
                        <div className="pa-step-name">{row.job_name || row.name}</div>
                        {row.job_id != null && <div className="pa-step-meta">JOB-{row.job_id}</div>}
                      </td>
                      {ENERGIES.map((e) => {
                        const { capacity, standing } = cellFor(row, e);
                        const holder = isHolder(row, e);
                        return (
                          <td key={e}>
                            <div className={`pa-capi-meter${holder ? " top" : ""}`}>
                              <span
                                className="pa-capi-track"
                                title={`capacity ${num(capacity)} · standing ${pct(standing)}`}
                              >
                                <i className={`stand dim-${e}`} style={{ width: `${standing ?? 0}%` }} />
                                <i className={`cap dim-${e}`} style={{ width: `${capacity ?? 0}%` }} />
                              </span>
                              <span className="pa-capi-val">
                                {num(capacity)}
                                <em>{pct(standing)}</em>
                              </span>
                              {holder && <span className="pa-tag holder">holder</span>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PaeiCapiTab;
